import {createSlice} from "@reduxjs/toolkit";

import _ from "lodash";
import moment from "moment";
import API from "../../app/api";


import config from "../../app/config";
import DocumentUtils from "../../util/document";
import Formatter from "../../util/formatter";
import {setGlobalVariable} from './globals';
import {getGroup, getInitiallyOpen} from './preferences';

export const documentsSlice = createSlice({
    name: "documents",
    initialState: {
        value: undefined,
        loading: undefined,
        patientId: undefined,
        studies: undefined,
        studiesGroup: undefined,
        studiesUngrouped: undefined,
        selectedDocuments: undefined,
        documentToPrev: undefined
    },
    reducers: {
        setDocuments: (state, documentsDescriptor) => {
            state.value = documentsDescriptor.payload.documents;
            state.patientId = documentsDescriptor.payload.patientId;
            if (!documentsDescriptor.payload.documents) {
                state.studies = undefined;
            }
        },
        setLoading: (state, loading) => {
            state.loading = loading.payload;
        },
        setDocumentToPrev: (state, documentToPrev) => {
            state.documentToPrev = documentToPrev.payload;
        },
        setStudies: (state, studiesDescriptor) => {
            state.studies = studiesDescriptor.payload;
        },
        setSelectedDocuments: (state, selectedDocuments) => {
            state.selectedDocuments = selectedDocuments.payload;
        },
        setStudiesUngrouped: (state, studiesDescriptor) => {
            state.studiesUngrouped = studiesDescriptor.payload;
        },
        setStudiesGroup: (state, studiesGroupDescriptor) => {
            // date
            // type
            // location
            // operator
            // tagged ???

            state.studiesGroup = studiesGroupDescriptor.payload;
        },
        setExpanded: (state, expandedDescriptor) => {
            if (state.studies) {
                DocumentUtils.expand(state.studies, expandedDescriptor.payload.study, expandedDescriptor.payload.expand);
            }
        },
        setSelected: (state, selectedDescriptor) => {
            if (state.studies) {
                DocumentUtils.select(state.studies, selectedDescriptor.payload.study, selectedDescriptor.payload.select);
            }
        },
        setLoadError: (state, documentDescriptor) => {
            if (state.studies) {
                DocumentUtils.setDocumentError(state.studies, documentDescriptor.payload, true);
            }
        },
        removeDocument: (state, document) => {
            const docId = document.payload ? document.payload.__id : undefined;
            if (docId) {
                if (state.value && state.value.__data && state.value.__data.length > 0) {
                    const newValue = [];
                    _.forEach(state.value.__data, doc => {
                        if (doc.__id !== docId) {
                            newValue.push(doc);
                        }
                    });
                    state.value.__data = newValue;
                }
            }
        },
        updateDocument: (state, document) => {
            const docId = document.payload ? document.payload.__id : undefined;
            if (docId) {
                if (state.value && state.value.__data && state.value.__data.length > 0) {
                    const newValue = [];
                    _.forEach(state.value.__data, doc => {
                        if (doc.__id === docId) {
                            newValue.push(document.payload);
                        } else {
                            newValue.push(doc);
                        }
                    });
                    state.value.__data = newValue;
                }
            }
        },
        doReorderStudyDocuments: (state, action) => {
            const study = action.payload.study;
            const sourceIndex = action.payload.sourceIndex
            const destinationIndex = action.payload.destinationIndex;

            if (study) {
                DocumentUtils.reorderStudyDocuments(state.studies, study, sourceIndex, destinationIndex);
            }
        },
    },
});

export const {
    setDocuments, setLoading, setStudies, setExpanded, setSelected, setLoadError,
    setStudiesUngrouped, setSelectedDocuments,setDocumentToPrev,
    setStudiesGroup, updateDocument, removeDocument, doReorderStudyDocuments,
} = documentsSlice.actions;

export const documents = state => state.documents.value;
export const loading = state => state.documents.loading;
export const studies = state => state.documents.studies;
export const studiesUngrouped = state => state.documents.studiesUngrouped;
export const studiesGroup = state => state.documents.studiesGroup;
export const getSelectedDocuments = state => state.documents.selectedDocuments;
export const getDocumentToPrev = state => state.documents.documentToPrev;

export const regroupStudies = (groupMethod) => (dispatch, getState) => {
    groupStudies(getState().documents.studiesUngrouped, groupMethod, dispatch);
};

export const getStudies  = () => (dispatch, getState) =>{
    return getState().documents.studies;
};

function groupStudies(documentsList, groupMethod, dispatch, init = false, isResearch = false) {
    documentsList = _.cloneDeep(documentsList);

    let grouped = undefined;
    if(init)
        groupMethod = dispatch(getGroup(isResearch));

    if (documentsList && (!groupMethod || groupMethod === "date")) {
        // FIXME: we do need to add other groupings here as well
        grouped = DocumentUtils.groupObjectByDate(documentsList, "startDate", "year");
        grouped = DocumentUtils.groupObjectByDate(grouped, "startDate", "month");
    } else {
        // eslint-disable-next-line default-case
        switch (groupMethod) {
            case "location":
                grouped = DocumentUtils.groupObjectByProperty(documentsList, "__location");
                break;
            case "type":
                grouped = DocumentUtils.groupObjectByProperty(documentsList, "__typeString");
                break;
            case "operator":
                grouped = DocumentUtils.groupObjectByProperty(documentsList, "__operator");
                break;
            case "patientId":
                grouped = DocumentUtils.groupObjectByProperty(documentsList, "__patientId");
                break;
            case "researchStudyName":
                grouped = DocumentUtils.groupObjectByProperty(documentsList, "__researchStudyName");
                break;
        }
    }

    DocumentUtils.prepareStudies(grouped);
    grouped = _.reverse(_.sortBy(grouped, d => d.startDate ? moment(d.startDate) : null));

    let index = 0;
    DocumentUtils.enumStudy(grouped, (study) => {
        if (index < dispatch(getInitiallyOpen())) {
            study.__expanded = true;
        }
        index++;
    });

    if(init) {
        DocumentUtils.enumStudy(dispatch(getStudies()), (study) => {
            DocumentUtils.enumStudy(grouped, (studyNew) => {
                if (study.groupId === studyNew.groupId && study.__expanded) {
                    studyNew.__expanded = true;
                }
            });

        });
    }

    dispatch(setStudies(grouped));
    dispatch(setStudiesGroup(groupMethod ? groupMethod : "date"));
}

export function getGroupId(document) {
    return document.metadata.reportGroupId ?
        document.metadata.reportGroupId :
        `${document.metadata.location}_${document.metadata.procedureName}_${moment(document.startDate).format("DDMMYYYY")}`;
}

export function getGroupIdForResearch(document) {
    return document.metadata.reportStartDateString ?
        `${document.metadata.research?.studyName}_${document.metadata.patientId}_${document.reportStartDateString}`.toLowerCase() :
        `${document.metadata.research?.studyName}_${document.metadata.patientId}_${moment(document.startDate).format("DDMMYYYY")}`.toLowerCase();
}

function setupStudies(t, documentsList, dispatch, groupMethod, isResearch) {
    documentsList = _.cloneDeep(documentsList);
    _.forEach(documentsList, d => {
        d.studyId = d.metadata && d.metadata.studyId ? d.metadata.studyId : "";
        d.groupId = isResearch ? getGroupIdForResearch(d) : (d.studyId || getGroupId(d));
        // TODO: handle metadata.relatedDocument
        // TODO: if exist - create 2 way link to open those 2 as OCT ?
    });
    // TODO: it's done at server/API side
    //documentsList = _.sortBy(documentsList, "startDate");

    const groupedByStudy = _.groupBy(documentsList, "groupId");

    const groupedArray = [];
    _.forEach(groupedByStudy, (documents, groupId) => {
        const startDate = isResearch ?
          _.maxBy(documents, d => moment(d.startDate)).startDate :
          _.minBy(documents, d => moment(d.startDate)).startDate;

        let study = {
            study: documents[0].studyId,
            groupId: documents[0].groupId,
            documents,
            startDate,
            __display: documents[0].groupId, // TODO: maybe something more complex - like a method?
            __isStudy: true,
        };

        study.__operator = Formatter.getStudyOperator(t, study);
        study.__location = Formatter.getStudyLocation(t, study);
        study.__typeString = Formatter.getStudyType(t, study);
        study.__colorString = Formatter.getColorByType(t, study);

        const studyData = Formatter.getStudyData(t, study);
        study.__patientId = studyData.__patientId;
        study.__researchStudyName = studyData.__researchStudyName;

        groupedArray.push(study);
    });

    // TODO: sort documents by
    // TODO: series
    // TODO: series number within if exists
    // TODO: date (acquisition?)

    // TODO: it's done at server/API side
    // sort inside a group only
    // _.forEach(groupedArray, group => {
    //     group.documents = _.sortBy(group.documents, document => {
    //         return document.metadata ? document.metadata.seriesId : undefined;
    //     });
    // });
    documentsList = _.reverse(_.sortBy(groupedArray, "startDate"));

    dispatch(setStudiesUngrouped(documentsList));

    groupStudies(documentsList, groupMethod, dispatch, true, isResearch);
}

export const loadDocumentsForPatientById = (t, patientId, reloadDocs, filter) => (dispatch, getState) => {
    if (reloadDocs || getState().documents.patientId !== patientId.toUpperCase()) {
        if (getState().documents.loading !== patientId.toUpperCase()) {
            dispatch(setLoading(patientId.toUpperCase()));
            dispatch(setGlobalVariable({
                name: "scrollPositionEyonix",
                value: 0,
            }));
            filter = filter || getState().globals.variables["extraDocFilter"];
            API.getDocumentsForPatientByID({patientId, filter}).then(documents => {
                // documents.__data = [];
                if (getState().documents.loading === patientId.toUpperCase()) {
                    // FIXME: remove this !!!!
                    const documentsToSave = _.cloneDeep(documents);
                    const showEmptyDocuments = !!config.modules.patient?.showEmptyDocuments?.enabled;
                    const publishDelay = config.modules.patient?.showEmptyDocuments?.delay || 0;
                    _.remove(documentsToSave.__data, doc => {
                        const docDate = doc.dateCreated || doc.startDate;
                        if (showEmptyDocuments && docDate &&
                            moment().diff(docDate, "seconds") >= publishDelay) {
                            return false;
                        }
                        return doc.pageCount === 0;
                    });
                    documents = documentsToSave;

                    // set procedureName if not set already
                    _.forEach(documents.__data, document => {
                        if (document && document.metadata) {
                            if (!document.metadata.procedureName && document.docType) {
                                document.metadata.procedureName = document.docType.name;
                            }
                        }
                    });
                    
                    // may be that some other request will hang and we do not want this data !
                    dispatch(setDocuments({
                        patientId: patientId.toUpperCase(),
                        documents: documents,
                    }));
                    if (documents) {
                        const isResearch = getState().globals.variables["isResearch"];
                        setupStudies(t, documents.__data, dispatch, isResearch ? "patientId" : undefined, isResearch);
                    }
                    dispatch(setLoading(undefined));
                }
            }).catch(function (error) {
                // TODO: error handler !!!
                console.error(error);
                dispatch(setLoading(undefined));
            });
        }
    }
};

export const reorderStudyDocuments = ({ study, sourceIndex, destinationIndex ,t}) => (dispatch, getState) => {
    dispatch(doReorderStudyDocuments({ study, sourceIndex, destinationIndex }));

    const modifiedStudy = DocumentUtils.getStudy(getState().documents.studies, study);
    let documents = _.cloneDeep(getState().documents.value);

    _.forEach(modifiedStudy.documents, (doc) => {
        let index = _.findIndex(documents.__data, (d)=>{
            return d.externalId === doc.externalId;
        });
        if (index > -1) {
            documents.__data[index].metadata.sortOrder = doc.__position;
        }
    });

    documents.__data = _.sortBy(documents.__data, ['metadata.sortOrder','metadata.seriesId',"startDate"]);

    dispatch(setDocuments({
        patientId: getState().patientId,
        documents: documents,
    }));

    if (documents) {
        //const isResearch = getState().globals.variables["isResearch"];
        //setupStudies(t, documents.__data, dispatch, undefined, isResearch);
    }

    if (modifiedStudy) {
        const dataByDocumentId = {
            items: [],
        };
        _.forEach(modifiedStudy.documents, doc => {
            dataByDocumentId.items.push({
                docId: doc.__id,
                path: "sortOrder",
                metadata: doc.__position,
            });
        });
        if (dataByDocumentId.items.length > 0) {
            dispatch(setLoading({}));
            API.updateDocumentsData(dataByDocumentId).then(data => {
                dispatch(setLoading(undefined));
            }).catch(function (error) {
                // TODO: error handler !!!
                console.error("updateDocumentsData.error", error);
                dispatch(setLoading(undefined));
            });
        }
    }
};

export default documentsSlice.reducer;